import { supabase } from './supabaseClient'
import type { ResumeAnalysis, ResumeComparison, ResumeComparisonRecord } from '../types'

/**
 * Upload a resume file to Supabase Storage
 */
export async function uploadResume(file: File, userId: string): Promise<string> {
  // Validate file type
  const allowedTypes = ['application/pdf']
  if (!allowedTypes.includes(file.type)) {
    throw new Error('Only PDF files are supported')
  }

  // Validate file size (5MB max)
  const maxSize = 5 * 1024 * 1024 // 5MB
  if (file.size > maxSize) {
    throw new Error('File size exceeds 5MB limit')
  }

  // Generate unique file path
  const fileExt = file.name.split('.').pop() || 'pdf'
  const timestamp = Date.now()
  const fileName = `${userId}/resume-${timestamp}.${fileExt}`

  console.log('Uploading resume:', fileName)

  // Upload to Storage
  const { data, error } = await supabase.storage
    .from('resumes')
    .upload(fileName, file, {
      upsert: false,
      contentType: file.type,
    })

  if (error) {
    console.error('Upload error:', error)
    throw new Error(`Upload failed: ${error.message}`)
  }

  console.log('Upload successful:', data.path)
  return data.path
}

/**
 * Analyze a resume using the Edge Function
 */
export async function analyzeResume(filePath: string): Promise<ResumeAnalysis> {
  console.log('Analyzing resume:', filePath)

  // Get current session
  const {
    data: { session },
  } = await supabase.auth.getSession()

  if (!session) {
    throw new Error('No active session. Please log in again.')
  }

  const { data, error } = await supabase.functions.invoke<ResumeAnalysis>('analyze-resume', {
    body: { filePath },
    headers: {
      Authorization: `Bearer ${session.access_token}`,
    },
  })

  if (error) {
    console.error('Analysis error:', error)
    throw new Error(`Analysis failed: ${error.message}`)
  }

  if (!data) {
    throw new Error('No data returned from analysis')
  }

  console.log('Analysis complete')
  return data
}

/**
 * Fetch all resume analyses for a user
 */
export async function fetchUserAnalyses(userId: string): Promise<ResumeAnalysis[]> {
  const { data, error } = await supabase
    .from('resume_analyses')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })

  if (error) {
    console.error('Fetch analyses error:', error)
    throw new Error(`Failed to fetch analyses: ${error.message}`)
  }

  return data || []
}

/**
 * Fetch a single resume analysis by ID
 */
export async function fetchAnalysisById(analysisId: string, userId: string): Promise<ResumeAnalysis | null> {
  const { data, error } = await supabase
    .from('resume_analyses')
    .select('*')
    .eq('id', analysisId)
    .eq('user_id', userId)
    .single()

  if (error) {
    console.error('Fetch analysis error:', error)
    return null
  }

  return data
}

/**
 * Get count of analyses for current month
 */
export async function getMonthlyAnalysisCount(userId: string): Promise<number> {
  const now = new Date()
  const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1).toISOString()
  const endOfMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0, 23, 59, 59).toISOString()

  const { data, error } = await supabase
    .from('resume_analyses')
    .select('id', { count: 'exact', head: true })
    .eq('user_id', userId)
    .gte('created_at', startOfMonth)
    .lte('created_at', endOfMonth)

  if (error) {
    console.error('Count error:', error)
    return 0
  }

  return data?.length || 0
}

/**
 * Delete a resume file and clear its data (keeps the analysis record for history)
 */
export async function deleteResume(filePath: string, userId: string): Promise<void> {
  // Delete from storage
  const { error: storageError } = await supabase.storage.from('resumes').remove([filePath])

  if (storageError) {
    console.error('Storage delete error:', storageError)
    throw new Error(`Failed to delete file: ${storageError.message}`)
  }

  // Clear file_path and raw_text but keep the analysis record for history
  const { error: dbError } = await supabase
    .from('resume_analyses')
    .update({ file_path: null, raw_text: null })
    .eq('file_path', filePath)
    .eq('user_id', userId)

  if (dbError) {
    console.error('Database update error:', dbError)
    // Don't throw - file is already deleted from storage
  }

  console.log('Resume file deleted, analysis preserved:', filePath)
}

/**
 * Compare user's resume against a specific job application.
 * If builderResumeId is provided, that Resume Builder resume is used as the source.
 * Otherwise the most recent uploaded resume is used.
 */
export async function compareResumeToApplication(
  applicationId: string,
  builderResumeId?: string,
): Promise<ResumeComparison> {
  console.log('Comparing resume to application:', applicationId, { builderResumeId })

  // Get current session
  const {
    data: { session },
  } = await supabase.auth.getSession()

  if (!session) {
    throw new Error('No active session. Please log in again.')
  }

  const { data, error } = await supabase.functions.invoke<ResumeComparison>('compare-resume', {
    body: builderResumeId ? { applicationId, builderResumeId } : { applicationId },
    headers: {
      Authorization: `Bearer ${session.access_token}`,
    },
  })

  if (error) {
    console.error('Comparison error:', error)
    throw new Error(`Comparison failed: ${error.message}`)
  }

  if (!data) {
    throw new Error('No data returned from comparison')
  }

  // Check if API returned an error message
  if ('error' in data) {
    throw new Error((data as { error: string }).error)
  }

  console.log('Comparison complete, match score:', data.match_score)
  return data
}

/**
 * Fetch the most recent (current) resume analysis for a user
 * Only returns analyses where the file still exists (file_path is not null)
 */
export async function fetchLatestResume(userId: string): Promise<ResumeAnalysis | null> {
  const { data, error } = await supabase
    .from('resume_analyses')
    .select('*')
    .eq('user_id', userId)
    .not('file_path', 'is', null)
    .order('created_at', { ascending: false })
    .limit(1)
    .single()

  if (error) {
    if (error.code === 'PGRST116') {
      // No rows found
      return null
    }
    console.error('Fetch latest resume error:', error)
    throw new Error(`Failed to fetch latest resume: ${error.message}`)
  }

  return data
}

/**
 * Check if the current user has an uploaded resume (without requiring userId)
 */
export async function hasCurrentResume(): Promise<boolean> {
  const {
    data: { session },
  } = await supabase.auth.getSession()

  if (!session) {
    return false
  }

  const resume = await fetchLatestResume(session.user.id)
  return resume !== null
}

/**
 * Fetch all resume comparisons for a user
 */
export async function fetchUserComparisons(userId: string): Promise<ResumeComparisonRecord[]> {
  const { data, error } = await supabase
    .from('resume_comparisons')
    .select('*')
    .eq('user_id', userId)
    .order('created_at', { ascending: false })

  if (error) {
    console.error('Fetch comparisons error:', error)
    throw new Error(`Failed to fetch comparisons: ${error.message}`)
  }

  return data || []
}

/**
 * Fetch a single comparison by ID
 */
export async function fetchComparisonById(
  comparisonId: string,
  userId: string
): Promise<ResumeComparisonRecord | null> {
  const { data, error } = await supabase
    .from('resume_comparisons')
    .select('*')
    .eq('id', comparisonId)
    .eq('user_id', userId)
    .single()

  if (error) {
    if (error.code === 'PGRST116') {
      return null
    }
    console.error('Fetch comparison error:', error)
    throw new Error(`Failed to fetch comparison: ${error.message}`)
  }

  return data
}

/**
 * Delete a resume comparison
 */
export async function deleteComparison(comparisonId: string, userId: string): Promise<void> {
  const { error } = await supabase
    .from('resume_comparisons')
    .delete()
    .eq('id', comparisonId)
    .eq('user_id', userId)

  if (error) {
    console.error('Delete comparison error:', error)
    throw new Error(`Failed to delete comparison: ${error.message}`)
  }

  console.log('Comparison deleted:', comparisonId)
}
